import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, Form, Input, Typography } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';
import styled from 'styled-components';

import { MAAS_INPUT_PLACEHOLDER } from '@/delivery-type-plugin/capabilities/maas-init/form-messages';

import { IMAGE_STORAGE_VOLUMES_FIELD, type ImageStorageVolumeItem, parseImageStorageVolumesField } from './imageStorageVolumes';

interface ImageStorageSettingProps {
  form: FormInstance;
}

const FormItem = Form.Item;

/** 模型实例镜像存储：只读回显选中镜像的存储配置，不支持新增自定义存储 */
export function ImageStorageSetting({ form }: ImageStorageSettingProps) {
  const rawField = Form.useWatch(IMAGE_STORAGE_VOLUMES_FIELD, form);
  const items = useMemo(() => parseImageStorageVolumesField(rawField), [rawField]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <FormItem field={IMAGE_STORAGE_VOLUMES_FIELD} hidden style={{ display: 'none', margin: 0 }}>
        <Input />
      </FormItem>

      {!items.length ? (
        <Typography.Text type="secondary">当前推理镜像未配置额外存储</Typography.Text>
      ) : (
        items.map((item, index) => <ImageStorageVolumeCard key={`${item.mountPath}-${index}`} item={item} index={index} />)
      )}
    </div>
  );
}

interface ImageStorageVolumeCardProps {
  item: ImageStorageVolumeItem;
  index: number;
}

function ImageStorageVolumeCard({ item, index }: ImageStorageVolumeCardProps) {
  const { t } = useTranslation();
  const mountPathLabel = t('data-center:maas.model.mountPath');

  return (
    <Card style={{ border: '1px solid var(--color-border-2)', borderRadius: '8px' }}>
      <CardTitleRow>
        <span>{t('data-center:maas.storage.configure.index', { index: index + 1 })}</span>
      </CardTitleRow>
      <FormItem label={mountPathLabel}>
        <Input placeholder={MAAS_INPUT_PLACEHOLDER} value={item.mountPath} disabled />
      </FormItem>
      <Typography.Text type="secondary">{item.kind === 'ephemeral' ? t('common:ephemeral.volume') : '持久卷'}</Typography.Text>
    </Card>
  );
}

const CardTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-1);
  margin-bottom: 8px;
`;

export default ImageStorageSetting;
